import jsPDF from 'jspdf';
import React, { useEffect, useMemo, useRef, useState } from 'react';

// Default site details (fallback if not provided)
const DEFAULT_SITE_DETAILS = {
  name: 'Innoworx Dining Hub',
  address: '2nd Floor, Prestige Tech Park, Bengaluru - 560103',
  // phone: '+91 98765 43210',
  // email: 'care@innoworx.com',
};

const defaultFormatCurrency = (amount = 0) =>
  Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(amount);

const BillingPrint = ({ bill, isOpen, onClose, formatCurrency }) => {
  const previewRef = useRef(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const formatMoney = formatCurrency || defaultFormatCurrency;

  // Get site details from bill or use defaults
  const siteDetails = useMemo(() => {
    if (bill?.siteDetails) {
      return {
        name: bill.siteDetails.organizationName || DEFAULT_SITE_DETAILS.name,
        address: bill.siteDetails.address || DEFAULT_SITE_DETAILS.address,
        siteName: bill.siteDetails.siteName || '',
        // phone: DEFAULT_SITE_DETAILS.phone,
        // email: DEFAULT_SITE_DETAILS.email,
      };
    }
    return DEFAULT_SITE_DETAILS;
  }, [bill]);

  // Debug logging
  useEffect(() => {
    console.log('BillingPrint - isOpen:', isOpen);
    console.log('BillingPrint - bill:', bill);
    console.log('BillingPrint - bill type:', typeof bill);
    console.log('BillingPrint - bill keys:', bill ? Object.keys(bill) : 'null');
    console.log('BillingPrint - siteDetails:', siteDetails);
  }, [isOpen, bill, siteDetails]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const { readableDate, readableTime } = useMemo(() => {
    if (!bill?.generatedAt) {
      return { readableDate: '', readableTime: '' };
    }
    const previewDate = new Date(bill.generatedAt);
    return {
      readableDate: previewDate.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      }),
      readableTime: previewDate.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
      }),
    };
  }, [bill]);

  const handleDownloadPdf = async () => {
    if (!bill) {
      return;
    }
    try {
      setIsGeneratingPdf(true);
      
      // Helper function to format numbers for PDF - ensure clean numeric string
      const formatNumberForPdf = (amount) => {
        if (amount === null || amount === undefined) {
          return '0.00';
        }
        const num = Number(amount);
        if (isNaN(num) || !isFinite(num)) {
          return '0.00';
        }
        // Format to 2 decimal places
        const formatted = num.toFixed(2);
        // Return as clean string - ensure no special characters
        return formatted.toString();
      };
      
      // Helper function to safely convert text to string for PDF
      const safeText = (text) => {
        if (text === null || text === undefined) {
          return '';
        }
        // Convert to string using toString() method
        let str = String(text);
        // Remove any problematic Unicode characters
        str = str.replace(/[\u200B-\u200D\uFEFF\u00AD]/g, '');
        // Remove any HTML entities or special characters
        str = str.replace(/&[#\w]+;/g, '');
        // Ensure it's a clean primitive string
        return str;
      };
      
      // Helper function to add text directly to PDF - bypass any string issues
      const addTextDirect = (text, x, y, fontSize = 9, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        const cleanText = safeText(text);
        // Split text into characters and add individually if needed (last resort)
        // But first try normal method
        try {
          pdf.text(cleanText, x, y);
        } catch (e) {
          // If that fails, try character by character (shouldn't be needed)
          console.warn('Text rendering issue:', e);
          pdf.text(cleanText, x, y);
        }
      };
      
      // Create thermal receipt format (80mm width = 3 inches)
      const receiptWidth = 80; // 80mm (standard thermal receipt width)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [receiptWidth, 297] // 80mm width, auto height (A4 length)
      });
      
      const pageWidth = receiptWidth;
      const margin = 5; // 5mm margin on each side
      const contentWidth = pageWidth - (margin * 2);
      let yPosition = margin;
      const lineHeight = 5;
      const sectionSpacing = 3;
      
      // Helper function to center text
      const centerText = (text, fontSize = 10, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        const safeStr = safeText(text);
        if (typeof safeStr === 'string') {
          const textWidth = pdf.getTextWidth(safeStr);
          const xPosition = (pageWidth - textWidth) / 2;
          pdf.text(safeStr, xPosition, yPosition);
        }
        yPosition += lineHeight;
      };
      
      // Helper function to add left-aligned text
      const addText = (text, fontSize = 9, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        const safeStr = safeText(text);
        if (typeof safeStr === 'string') {
          pdf.text(safeStr, margin, yPosition);
        }
        yPosition += lineHeight;
      };
      
      // Helper function to add right-aligned text
      const addRightText = (text, fontSize = 9, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        const safeStr = safeText(text);
        if (typeof safeStr === 'string') {
          const textWidth = pdf.getTextWidth(safeStr);
          pdf.text(safeStr, pageWidth - margin - textWidth, yPosition);
        }
        yPosition += lineHeight;
      };
      
      // Helper function to add two-column text (left and right)
      const addTwoColumn = (leftText, rightText, fontSize = 9, leftBold = false, rightBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', leftBold ? 'bold' : 'normal');
        const safeLeftStr = safeText(leftText);
        if (safeLeftStr && typeof safeLeftStr === 'string') {
          addTextDirect(safeLeftStr, margin, yPosition, fontSize, leftBold);
        }
        
        pdf.setFont('helvetica', rightBold ? 'bold' : 'normal');
        const safeRightStr = safeText(rightText);
        if (safeRightStr && typeof safeRightStr === 'string') {
          const rightTextWidth = pdf.getTextWidth(safeRightStr);
          addTextDirect(safeRightStr, pageWidth - margin - rightTextWidth, yPosition, fontSize, rightBold);
        }
        yPosition += lineHeight;
      };
      
      // Helper function to draw dashed line
      const drawDashedLine = () => {
        const dashLength = 2;
        const gapLength = 1;
        let x = margin;
        while (x < pageWidth - margin) {
          pdf.line(x, yPosition, Math.min(x + dashLength, pageWidth - margin), yPosition);
          x += dashLength + gapLength;
        }
        yPosition += lineHeight;
      };
      
      // Header
      centerText(siteDetails.name, 12, true);
      yPosition += 2;
      // Display site name if available
      if (siteDetails.siteName) {
        centerText(siteDetails.siteName, 9);
        yPosition += 1;
      }
      centerText(siteDetails.address, 8);
      yPosition += sectionSpacing;
      drawDashedLine();
      yPosition += sectionSpacing;
      
      // Date and Time
      addTwoColumn('Date:', readableDate, 9);
      addTwoColumn('Time:', readableTime, 9);
      yPosition += sectionSpacing;
      drawDashedLine();
      yPosition += sectionSpacing;
      
      // Order Details
      addText('ORDER DETAILS', 9, true);
      addTwoColumn('Order No:', bill.orderNumber, 9);
      addTwoColumn('Bill No:', bill.billNumber, 9);
      if (bill.tableNumber) {
        addTwoColumn('Table No:', bill.tableNumber, 9);
      }
      yPosition += sectionSpacing;
      drawDashedLine();
      yPosition += sectionSpacing;
      
      // Customer Details
      if (bill.customerDetails?.name) {
        addText('CUSTOMER DETAILS', 9, true);
        addTwoColumn('Name:', bill.customerDetails.name, 9);
        if (bill.customerDetails.mobile) {
          addTwoColumn('Mobile:', bill.customerDetails.mobile, 9);
        }
        if (bill.customerDetails.email) {
          addTwoColumn('Email:', bill.customerDetails.email, 9);
        }
        yPosition += sectionSpacing;
        drawDashedLine();
        yPosition += sectionSpacing;
      }
      
      // Items Header
      addText('ITEMS', 9, true);
      yPosition += 2;
      
      // Items List
      bill.items.forEach((item, index) => {
        // Check if we need a new page
        if (yPosition > 280) {
          pdf.addPage();
          yPosition = margin;
        }
        
        const itemName = item.item || 'Item ' + (index + 1);
        const quantity = String(item.quantity || '-');
        const pricePerUnit = item.pricePerUnit ? formatNumberForPdf(parseFloat(item.pricePerUnit)) : '-';
        const subtotal = formatNumberForPdf(item.subtotal);
        
        // Item name (may wrap)
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        const itemLines = pdf.splitTextToSize(safeText(itemName), contentWidth);
        pdf.text(itemLines, margin, yPosition);
        yPosition += (itemLines.length * lineHeight);
        
        // Quantity, Price, Subtotal - build strings as simple concatenation
        const qtyLabel = '  Qty: ' + quantity;
        let priceText;
        if (pricePerUnit !== '-') {
          const priceNum = formatNumberForPdf(parseFloat(item.pricePerUnit));
          priceText = 'Price: Rs. ' + priceNum;
        } else {
          priceText = 'Price: -';
        }
        const subtotalNum = formatNumberForPdf(item.subtotal);
        const subtotalText = 'Subtotal: Rs. ' + subtotalNum;
        
        addTwoColumn(qtyLabel, priceText, 8);
        addRightText(subtotalText, 9, true);
        yPosition += 2;
      });
      
      yPosition += sectionSpacing;
      drawDashedLine();
      yPosition += sectionSpacing;
      
      // Check if we need a new page for summary
      if (yPosition > 250) {
        pdf.addPage();
        yPosition = margin;
      }
      
      // Discount Section
      if (bill.discountCode && bill.discountValue > 0) {
        addText('DISCOUNT', 9, true);
        addTwoColumn('Code:', String(bill.discountCode), 9);
        const discountNum = formatNumberForPdf(bill.discountValue);
        const discountValue = 'Rs. ' + discountNum;
        addTwoColumn('Value:', discountValue, 9, false, true);
        yPosition += sectionSpacing;
      }
      
      // Summary
      addText('SUMMARY', 9, true);
      yPosition += 2;
      const subtotalNum = formatNumberForPdf(bill.discountedSubtotal);
      const subtotalValue = 'Rs. ' + subtotalNum;
      addTwoColumn('Subtotal:', subtotalValue, 9);
      
      if (bill.cgstAmount > 0) {
        const cgstRate = String(bill.cgstRate || 0);
        const cgstLabel = 'CGST (' + cgstRate + '%):';
        const cgstNum = formatNumberForPdf(bill.cgstAmount);
        const cgstValue = 'Rs. ' + cgstNum;
        addTwoColumn(cgstLabel, cgstValue, 9);
      }
      
      if (bill.sgstAmount > 0) {
        const sgstRate = String(bill.sgstRate || 0);
        const sgstLabel = 'SGST (' + sgstRate + '%):';
        const sgstNum = formatNumberForPdf(bill.sgstAmount);
        const sgstValue = 'Rs. ' + sgstNum;
        addTwoColumn(sgstLabel, sgstValue, 9);
      }
      
      yPosition += sectionSpacing;
      drawDashedLine();
      yPosition += sectionSpacing;
      
      // Total
      const totalNum = formatNumberForPdf(bill.grandTotal);
      const totalValue = 'Rs. ' + totalNum;
      addTwoColumn('TOTAL:', totalValue, 12, true, true);
      yPosition += sectionSpacing;
      drawDashedLine();
      yPosition += sectionSpacing * 2;
      
      // Footer
      centerText('Thank you for your visit!', 9);
      yPosition += 2;
      centerText('Visit us again', 8);
      yPosition += sectionSpacing * 2;
      
      pdf.save(`${bill.billNumber || 'bill'}.pdf`);
    } catch (error) {
      console.error('Failed to export bill PDF', error);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };

  if (!isOpen) {
    console.log('BillingPrint: Not open, returning null');
    return null;
  }

  if (!bill) {
    console.error('BillingPrint: Bill is null or undefined!', { isOpen, bill });
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
        <div className="rounded-2xl bg-white p-6 text-center">
          <p className="text-lg font-semibold text-red-600">Error: Bill data is missing</p>
          <p className="mt-2 text-sm text-gray-600">Please try generating the bill again.</p>
          <button
            onClick={onClose}
            className="mt-4 rounded-full bg-red-500 px-6 py-2 text-sm font-semibold text-white hover:bg-red-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Validate required bill fields
  if (!bill.items || !Array.isArray(bill.items) || bill.items.length === 0) {
    console.error('BillingPrint: Bill items are missing or invalid!', { 
      items: bill.items,
      itemsType: typeof bill.items,
      itemsIsArray: Array.isArray(bill.items),
      itemsLength: bill.items?.length
    });
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
        <div className="rounded-2xl bg-white p-6 text-center">
          <p className="text-lg font-semibold text-red-600">Error: Bill items are missing</p>
          <p className="mt-2 text-sm text-gray-600">The bill must contain at least one item.</p>
          <button
            onClick={onClose}
            className="mt-4 rounded-full bg-red-500 px-6 py-2 text-sm font-semibold text-white hover:bg-red-600"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black/70 overflow-hidden">
      {/* Fixed Header */}
      <div className="flex-shrink-0 px-4 pt-6 pb-4">
        <div className="mx-auto w-full max-w-5xl">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm font-semibold uppercase tracking-widest text-white/80">
              Bill Preview
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-white/40 bg-white/15 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/25"
              >
                Close
              </button>
              <button
                type="button"
                onClick={handleDownloadPdf}
                disabled={isGeneratingPdf}
                className="rounded-full bg-emerald-500 px-6 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isGeneratingPdf ? 'Preparing PDF...' : 'Download PDF'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto px-4 pb-6">
        <div className="mx-auto w-full max-w-5xl">
          <div
            ref={previewRef}
            className="mx-auto w-full max-w-[900px] rounded-[32px] bg-white p-3 text-gray-800 shadow-2xl sm:p-4"
          >
          <div className="rounded-[28px] border border-gray-200 bg-gradient-to-r from-blue-50 via-white to-purple-50 p-6 text-center">
            <p className="text-2xl font-semibold text-slate-900">
              {siteDetails.name}
            </p>
            {siteDetails.siteName && (
              <p className="mt-1 text-base font-medium text-slate-700">
                {siteDetails.siteName}
              </p>
            )}
            <p className="mt-2 text-sm text-slate-600">{siteDetails.address}</p>
          </div>

          <div className="mt-6 flex flex-wrap items-start justify-between text-sm text-gray-600">
            <div>
              <span className="font-semibold text-gray-800">Date:</span>{' '}
              {readableDate}
            </div>
            <div>
              <span className="font-semibold text-gray-800">Time:</span>{' '}
              {readableTime}
            </div>
          </div>

          <div className="mt-4 grid gap-4 rounded-[24px] border border-gray-200 p-4 text-sm sm:grid-cols-2">
            <div className="space-y-1 text-gray-700">
              <p>
                <span className="font-semibold text-gray-900">Order No:</span>{' '}
                {bill.orderNumber}
              </p>
              <p>
                <span className="font-semibold text-gray-900">Bill No:</span>{' '}
                {bill.billNumber}
              </p>
              <p>
                <span className="font-semibold text-gray-900">Table No:</span>{' '}
                {bill.tableNumber || '-'}
              </p>
            </div>
            <div className="space-y-1 text-gray-700">
              <p>
                <span className="font-semibold text-gray-900">Customer:</span>{' '}
                {bill.customerDetails?.name}
              </p>
              <p>
                <span className="font-semibold text-gray-900">Mobile:</span>{' '}
                {bill.customerDetails?.mobile || '-'}
              </p>
              <p>
                <span className="font-semibold text-gray-900">Email:</span>{' '}
                {bill.customerDetails?.email || '-'}
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-100 mt-3">
            <div className="grid grid-cols-4 gap-2 border-b border-gray-100 bg-gray-50 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <span>Item</span>
              <span className="text-center">Qty</span>
              <span className="text-center">Price/Unit</span>
              <span className="text-right">Subtotal</span>
            </div>
            {bill.items.map((row, index) => (
              <div
                key={`preview-row-${index}`}
                className="grid grid-cols-4 gap-2 border-b border-gray-100 px-4 py-3 text-sm last:border-b-0"
              >
                <span className="font-medium text-gray-800">
                  {row.item || `Item ${index + 1}`}
                </span>
                <span className="text-center text-gray-600">
                  {row.quantity || '-'}
                </span>
                <span className="text-center text-gray-600">
                  {row.pricePerUnit
                    ? formatMoney(parseFloat(row.pricePerUnit))
                    : '-'}
                </span>
                <span className="text-right font-semibold text-gray-900">
                  {formatMoney(row.subtotal)}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-col gap-4 lg:flex-row">
            <div className="flex-1 rounded-[28px] border border-gray-200 p-4">
              <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Discount
              </p>
              <div className="mt-3 text-gray-700">
                <p className="text-sm">
                  Code:{' '}
                  <span className="font-semibold text-gray-900">
                    {bill.discountCode || 'N/A'}
                  </span>
                </p>
                <p className="text-sm">
                  Value:{' '}
                  <span className="font-semibold text-emerald-600">
                    {formatMoney(bill.discountValue)}
                  </span>
                </p>
              </div>
              <div className="mt-4 rounded-2xl bg-gray-50 px-4 py-3 text-sm">
                <p className="text-gray-500">Amount after discount</p>
                <p className="text-xl font-semibold text-gray-900">
                  {formatMoney(bill.discountedSubtotal)}
                </p>
              </div>
            </div>
            <div className="w-full rounded-[28px] border border-gray-200 p-4 lg:w-72">
              <p className="text-sm font-semibold uppercase tracking-wide text-gray-500">
                Summary
              </p>
              <div className="mt-4 space-y-3 text-sm text-gray-700">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span className="font-semibold">
                    {formatMoney(bill.discountedSubtotal)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>CGST ({bill.cgstRate || 0}%)</span>
                  <span className="font-semibold">
                    {formatMoney(bill.cgstAmount)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>SGST ({bill.sgstRate || 0}%)</span>
                  <span className="font-semibold">
                    {formatMoney(bill.sgstAmount)}
                  </span>
                </div>
                <div className="flex justify-between border-t border-gray-100 pt-3 text-base font-bold text-gray-900">
                  <span>Total</span>
                  <span>{formatMoney(bill.grandTotal)}</span>
                </div>
              </div>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BillingPrint;


